import { useEffect, useState } from 'react';
import { listPublicStories } from '@/services/storyRepo';

// Which stories the landing page features, in one place.
//
// Oldest-first, deliberately: the founding set written when the library was
// seeded is the strongest and most representative work here, and a first-time
// visitor should meet those rather than whatever happened to be approved last
// week. This is an editorial policy, not a technical constraint — "newest 6"
// would be equally arbitrary.
//
// Known cost: the homepage is now effectively frozen. As the library grows,
// these same stories stay and no new contributor's story ever surfaces here.
// The graduation path is a `featured_rank` column on km_stories set from the
// moderation dashboard; at that point this constant goes away and the query
// below orders by that column instead. Until curation exists, the founding
// set is the better default.
const HOME_ORDER_ASCENDING = true;

/**
 * Loads the landing page's featured stories, unfiltered.
 *
 * `total` comes back from the same query's exact count, so callers can say
 * "Browse all 18 stories" without a second round-trip.
 *
 * Deliberately not cached at module level like useTagOptions: tag dictionaries
 * are effectively static, but the story count changes whenever a story is
 * approved, and a stale homepage is worse than one extra query per visit.
 */
export function useHomeStories(limit) {
  const [stories, setStories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    listPublicStories({ limit, offset: 0, ascending: HOME_ORDER_ASCENDING })
      .then(({ stories: fetched, total: fetchedTotal }) => {
        if (cancelled) return;
        setStories(fetched);
        setTotal(fetchedTotal);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load home stories:', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [limit]);

  return { stories, total, loading, error };
}
